import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import DropdownTableHeader from './DropdownTableHeader';
import SanitizedTableCell from './SanitizedTableCell';
import StatusTableCell from './StatusTableCell';
import FileTableCell from './FileTableCell';
import { getLocaleDateTime } from '../../helpers/dateTimeHelper';
import html2canvas from 'html2canvas';
import { FaCamera, FaEye, FaDownload, FaShareAlt } from "react-icons/fa";

function LogTable({
    loading,
    userconfig,
    tableHeaders,
    selectedValues,
    dropdownOptions,
    handleDropdownChange,
    loadingDropdownOptions,
    logMessageTable,
    config,
    modalOpen,
    openModal,
    closeModal,
    handleResendFile,
    accounts,
    instance,
    onTransactionTagClick,
    transactionTagFilter,
    handleClearTransactionTagFilter
}) {

    // Key for localStorage
    const storageKey = `logTableVisibleColumns_${userconfig}`;

    // Load from localStorage or default to all true
    const getInitialVisibleColumns = () => {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Ensure length matches headers
                if (Array.isArray(parsed) && parsed.length === tableHeaders.length) {
                    return parsed;
                }
            } catch { }
        }
        return tableHeaders.map(() => true);
    };

    const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);
    const [checkedRows, setCheckedRows] = useState([]);
    const tableRef = useRef(null);

    const data = Array.from({ length: 10 }, (_, i) =>
        Array.from({ length: 5 }, (_, j) => `Row ${i + 1}, Col ${j + 1}`)
    );

    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
    }, [visibleColumns, storageKey]);

    // toggle row selection
    const handleCheck = (index) => {
        setCheckedRows((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    const handleSnapshot = async () => {
        const table = tableRef.current;
        if (!table) return;

        const tbodyRows = Array.from(table.tBodies[0].rows);
        const theadRow = table.tHead?.rows[0];
        if (!theadRow) return;

        // Find the index of the Snapshot column by header title
        const snapshotColIndex = shownHeaders.findIndex(
            (header) => header.title === "Snapshot"
        );

        // Hide unchecked rows
        tbodyRows.forEach((row, idx) => {
            if (!checkedRows.includes(idx)) row.style.display = "none";
        });

        // Hide the Snapshot column if it exists
        if (snapshotColIndex >= 0) {
            // Hide header
            theadRow.cells[snapshotColIndex].style.display = "none";
            // Hide each row's cell
            tbodyRows.forEach((row) => {
                row.cells[snapshotColIndex].style.display = "none";
            });
        }

        const replacedElements = [];

        // Replace <select> dropdowns with styled <span>
        table.querySelectorAll("select").forEach((sel) => {
            const span = document.createElement("span");
            span.textContent = sel.options[sel.selectedIndex]?.text || "";
            span.style.padding = "2px 6px";
            span.style.border = "1px solid #ccc";
            span.style.borderRadius = "4px";
            span.style.background = "#f9f9f9";
            span.style.fontSize = window.getComputedStyle(sel).fontSize;
            span.style.fontFamily = window.getComputedStyle(sel).fontFamily;

            sel.style.display = "none";
            sel.parentNode.insertBefore(span, sel);

            replacedElements.push({ original: sel, replacement: span });
        });

        // Capture with html2canvas
        const canvas = await html2canvas(table, {
            scale: 2, useCORS: true, allowTaint: true, ignoreElements: (element) => {
                return element.classList.contains("filter-icon"); // skip icons
            }
        });
        const dataUrl = canvas.toDataURL("image/png");

        // Restore everything
        replacedElements.forEach(({ original, replacement }) => {
            replacement.remove();
            original.style.display = "";
        });

        // Restore all rows and column
        tbodyRows.forEach((row) => (row.style.display = ""));
        if (snapshotColIndex >= 0) {
            theadRow.cells[snapshotColIndex].style.display = "";
            tbodyRows.forEach((row) => {
                row.cells[snapshotColIndex].style.display = "";
            });
        }

        // Open snapshot in new tab
        const newTab = window.open();
        newTab.document.body.innerHTML = `<img src="${dataUrl}" />`;
    };


    const shownHeaders = tableHeaders.filter((_, idx) => visibleColumns[idx]);

    return (
        <>
            {loading ? (
                <div className="loading-container">
                    <FontAwesomeIcon icon={faSpinner} className="spinner" size="3x" spin />
                    <p className="loading-text">Loading...</p>
                </div>
            ) : (
                <div className="table-container">

                    {transactionTagFilter && (
                        <div style={{ marginBottom: 10 }}>
                            <span>
                                Filtering by Transaction Tag: <strong>{transactionTagFilter}</strong>
                            </span>
                            <button onClick={handleClearTransactionTagFilter} style={{ marginLeft: 10 }}>
                                Clear Filter
                            </button>
                        </div>
                    )}

                    <div className="column-toggle-menu" style={{ marginBottom: 10 }}>
                        <strong>Show/Hide Columns:</strong>
                        {tableHeaders.map((header, idx) => (
                            <label key={idx} style={{ marginLeft: 10 }}>
                                <input
                                    type="checkbox"
                                    checked={visibleColumns[idx]}
                                    onChange={() => {
                                        setVisibleColumns(cols =>
                                            cols.map((v, i) => i === idx ? !v : v)
                                        );
                                    }}
                                />
                                {header.title}
                            </label>
                        ))}
                    </div>

                    <table id="logMessageTableId" className="message-logs-table" ref={tableRef}>
                        <thead>
                            <tr>
                                {shownHeaders.map((header, index) =>
                                    header.isDropdown ? (
                                        <DropdownTableHeader
                                            key={index}
                                            title={`${header.title}:`}
                                            selectedValue={selectedValues[header.field]}
                                            options={dropdownOptions[header.field]}
                                            handleDropdownChange={(e) => handleDropdownChange(header.field, e.target.value)}
                                            loadingDropdownOptions={loadingDropdownOptions}
                                            style={{ maxWidth: header.maxWidth }}
                                        />
                                    ) : header.title === "Snapshot" ? (
                                            <th className="camera-cell">
                                                <button
                                                    onClick={handleSnapshot}
                                                    title="Take Snapshot" // tooltip only on hover
                                                    className="camera-button"
                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#0056b3")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#007bff")}
                                                >
                                                    <FaCamera size={10} />
                                                </button>
                                            </th>
                                    ) : (
                                        <th key={index} tabIndex="0" style={{ maxWidth: header.maxWidth }}>
                                            {header.title}
                                        </th>
                                    )
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {logMessageTable.map((row, rowIdx) => (
                                <tr key={rowIdx} className={rowIdx % 2 === 0 ? "even-row" : "odd-row"}>
                                    {tableHeaders.map((header, colIdx) => {
                                        if (!visibleColumns[colIdx]) return null;

                                        // Render the correct cell based on header.title or header.field
                                        switch (header.title) {
                                            case "#":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{rowIdx + 1}</td>;
                                            case "Processed":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{getLocaleDateTime(row.processed)}</td>;
                                            case "Sender":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{row.sender}</td>;
                                            case "Receiver":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{row.receiver}</td>;
                                            case "Transaction Type":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{row.transactionType}</td>;
                                            case "Message Type":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{row.messageType}</td>;
                                            case "Transaction Tag":
                                                return (
                                                    <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>
                                                        <span
                                                            style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
                                                            onClick={() => onTransactionTagClick && onTransactionTagClick(row.transactionTag)}
                                                        >
                                                            {row.transactionTag}
                                                        </span>
                                                    </td>
                                                );
                                            case "Description":
                                                return <SanitizedTableCell key={colIdx} stringValue={row.description} style={{ maxWidth: header.maxWidth }} />;
                                            case "Status":
                                                return <StatusTableCell key={colIdx} status={row.status} />;
                                            case "FileName":
                                                return (
                                                    <FileTableCell
                                                        key={colIdx}
                                                        row={row}
                                                        config={config}
                                                        modalOpen={modalOpen}
                                                        openModal={openModal}
                                                        closeModal={closeModal}
                                                        onResendFile={handleResendFile}
                                                        accounts={accounts}
                                                        instance={instance}
                                                        style={{ maxWidth: header.maxWidth }}
                                                    />
                                                );
                                            case "Scope":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{row.scope}</td>;
                                            case "System":
                                                return <td key={colIdx} tabIndex="0" style={{ maxWidth: header.maxWidth }}>{row.system}</td>;
                                            default:
                                                return null;
                                        }
                                    })}

                                    <td tabIndex="0" style={{ maxWidth: "10px" }}>
                                        <input
                                            type="checkbox"
                                            checked={checkedRows.includes(rowIdx)}
                                            onChange={() => handleCheck(rowIdx)}
                                        />
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

export default LogTable;
